import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class PdfImageService {

  constructor() { }

  /**
   * Load image for PDF with CORS-safe approach using multiple strategies
   * @param imageUrl URL of the image to load
   * @param doc PDF document object
   * @param addContentCallback Callback function to execute after image is loaded
   * @returns Promise that resolves when image loading is complete
   */
  public async loadImageForPDF(imageUrl: string, doc: any, addContentCallback: (startY: number, logoWidth?: number) => void): Promise<void> {
    console.log('Loading image for PDF:', imageUrl);
    
    // Default Parkbot logo as fallback
    const defaultLogo = 'assets/img/logo_new.png';
    
    // If no image URL provided, use default logo
    if (!imageUrl) {
      console.log('No image URL provided, using default logo');
      this.loadImageDirectFallback(defaultLogo, doc, addContentCallback);
      return;
    }
    
    // Strategy 1: Try CORS proxy for Firebase Storage
    if (imageUrl.includes('firebasestorage.googleapis.com')) {
      console.log('Detected Firebase Storage URL, trying CORS proxy...');
      await this.loadImageWithCorsProxy(imageUrl, doc, addContentCallback);
      return;
    }
    
    // Strategy 2: Try direct fetch for other URLs
    try {
      console.log('Trying direct fetch...');
      const response = await fetch(imageUrl, {
        mode: 'cors',
        headers: {
          'Accept': 'image/*'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const blob = await response.blob();
      await this.processImageBlob(blob, doc, addContentCallback);
      
    } catch (error) {
      console.error('Direct fetch failed:', error);
      console.log('Trying fallback method...');
      this.loadImageDirectFallback(imageUrl, doc, addContentCallback);
    }
  }

  /**
   * Load Firebase Storage image using CORS proxy
   */
  private async loadImageWithCorsProxy(firebaseUrl: string, doc: any, addContentCallback: (startY: number, logoWidth?: number) => void): Promise<void> {
    try {
      // Use allorigins.win as CORS proxy (free service)
      const proxyUrl = `https://api.allorigins.win/raw?url=${encodeURIComponent(firebaseUrl)}`;
      console.log('Using proxy URL:', proxyUrl);
      
      const response = await fetch(proxyUrl);
      
      if (!response.ok) {
        throw new Error(`Proxy fetch failed: ${response.status}`);
      }
      
      const blob = await response.blob();
      console.log('Image loaded via proxy, size:', blob.size);
      
      await this.processImageBlob(blob, doc, addContentCallback);
      
    } catch (error) {
      console.error('CORS proxy failed:', error);
      console.log('Trying alternative proxy...');
      
      try {
        // Alternative: cors-anywhere (if available)
        const altProxyUrl = `https://cors-anywhere.herokuapp.com/${firebaseUrl}`;
        const response = await fetch(altProxyUrl);
        
        if (response.ok) {
          const blob = await response.blob();
          await this.processImageBlob(blob, doc, addContentCallback);
          return;
        }
      } catch (altError) {
        console.error('Alternative proxy also failed:', altError);
      }
      
      // Final fallback - try to load local default logo
      console.log('All proxy methods failed, using default logo...');
      this.loadImageDirectFallback('assets/img/logo_new.png', doc, addContentCallback);
    }
  }

  /**
   * Process image blob and add to PDF
   */
  private async processImageBlob(blob: Blob, doc: any, addContentCallback: (startY: number, logoWidth?: number) => void): Promise<void> {
    try {
      const objectUrl = URL.createObjectURL(blob);
      const img = new Image();
      
      img.onload = () => {
        try {
          console.log('Image processed, dimensions:', img.width, 'x', img.height);
          
          // Create canvas for image processing
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          if (!ctx) {
            console.error('Canvas context not available');
            URL.revokeObjectURL(objectUrl);
            addContentCallback(15, 0);
            return;
          }
          
          // Calculate smaller size (max 30mm, maintain aspect ratio) - INCREASED FROM 20mm
          const maxSize = 110; // pixels (30mm at 96 DPI) - INCREASED SIZE
          let { width, height } = img;
          
          if (width > height) {
            if (width > maxSize) {
              height = (height * maxSize) / width;
              width = maxSize;
            }
          } else {
            if (height > maxSize) {
              width = (width * maxSize) / height;
              height = maxSize;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          
          // Draw and convert to base64
          ctx.drawImage(img, 0, 0, width, height);
          const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
          
          // Position on the left side
          const mmWidth = (width * 25.4) / 96;
          const mmHeight = (height * 25.4) / 96;
          const xPosition = 14; // Left margin position
          const yPosition = 10; // Top margin
          
          // Add to PDF
          doc.addImage(dataUrl, 'JPEG', xPosition, yPosition, mmWidth, mmHeight);
          
          // Cleanup
          URL.revokeObjectURL(objectUrl);
          
          console.log(`✅ Logo added successfully at (${xPosition}, ${yPosition}) size: ${mmWidth.toFixed(1)}x${mmHeight.toFixed(1)}mm`);
          
          // Adjust content positioning and pass logo width
          addContentCallback(yPosition + mmHeight + 10, mmWidth); // INCREASED MARGIN FROM 5 TO 10
          
        } catch (error) {
          console.error('Failed to process image:', error);
          URL.revokeObjectURL(objectUrl);
          addContentCallback(15, 0);
        }
      };
      
      img.onerror = () => {
        console.error('Failed to load processed image');
        URL.revokeObjectURL(objectUrl);
        addContentCallback(15, 0);
      };
      
      img.src = objectUrl;
      
    } catch (error) {
      console.error('Failed to process image blob:', error);
      addContentCallback(15, 0);
    }
  }

  /**
   * Fallback method for direct image loading
   */
  private loadImageDirectFallback(imageUrl: string, doc: any, addContentCallback: (startY: number, logoWidth?: number) => void): void {
    const img = new Image();
    
    img.onload = () => {
      try {
        console.log('Fallback: Image loaded, dimensions:', img.width, 'x', img.height);
        
        // Simple direct image addition
        const maxWidth = 35; // mm - INCREASED FROM 25mm
        const aspectRatio = img.height / img.width;
        const logoWidth = maxWidth;
        const logoHeight = maxWidth * aspectRatio;
        
        const xPosition = 14;
        const yPosition = 10;
        
        doc.addImage(img, 'JPEG', xPosition, yPosition, logoWidth, logoHeight);
        
        console.log('Fallback: Logo added to PDF');
        addContentCallback(yPosition + logoHeight + 10, logoWidth); // INCREASED MARGIN FROM 5 TO 10
        
      } catch (error) {
        console.error('Fallback also failed:', error);
        addContentCallback(15, 0);
      }
    };
    
    img.onerror = () => {
      console.error('Fallback: Failed to load image directly');
      addContentCallback(15, 0);
    };
    
    // Try without crossOrigin
    img.src = imageUrl;
  }
} 